import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import css from './LocationAutocompleteInput.module.css';
import { IconSpinner } from '../../components';
import {
  continentsListDe,
  continentsList,
  mostBookedDestinations,
} from '../../data/countries-list';
import { arrayOf, func, number, shape, string, object, node } from 'prop-types';
import { elb } from '@elbwalker/walker.js';
import { getStaticCountryData } from '../../data/countriesStaticData';

if (typeof window !== 'undefined') {
  window.elbLayer = window.elbLayer || [];
}

const getTouchCoordinates = nativeEvent => {
  const touch = nativeEvent && nativeEvent.changedTouches ? nativeEvent.changedTouches[0] : null;
  return touch ? { x: touch.screenX, y: touch.screenY } : null;
};

const truncateText = (text, maxLength = 24) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const LocationPredictionsList = ({
  rootClassName,
  className,
  children,
  predictions,
  geocoder,
  isGoogleMapsInUse,
  highlightedIndex,
  onSelectStart,
  onSelectMove,
  onSelectEnd,
  predictionsLoading,
  lang,
  search,
  handleClose,
}) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [hasSearchChanged, setHasSearchChanged] = useState(false);
  const [lastSearch, setLastSearch] = useState('');

  useEffect(() => {
    const checkScreenSize = () => {
      const isSmall = window.innerWidth <= 768;
      setIsSmallScreen(isSmall);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchString = typeof search === 'string' ? search : '';
      if (searchString !== lastSearch) {
        setHasSearchChanged(
          searchString !== '' && searchString !== null && searchString !== undefined
        );
        setLastSearch(searchString);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search, lastSearch]);

  useEffect(() => {
    if (predictions && predictions.length > 0) {
      setHasSearchChanged(false);
    }
  }, [predictions]);

  const isPredictionsEmpty = !predictions || predictions.length === 0;

  const handleItemClick = (prediction, event) => {
    event.preventDefault();
    elb('search search_id:' + prediction.name, {
      id: prediction.name,
      type: 'destination',
      interaction: 'click',
    });
    onSelectEnd(prediction);

    if (isSmallScreen) {
      handleClose?.();
    }
  };

  const handleItemMouseDown = event => {
    if (!isSmallScreen) {
      event.preventDefault();
      onSelectStart(getTouchCoordinates(event.nativeEvent));
    }
  };

  const handleItemKeyDown = (prediction, event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      elb('search select', {
        id: prediction.name,
        type: 'destination',
        interaction: 'keyboard',
      });
      onSelectEnd(prediction);
      handleClose?.();
    }
  };
  const renderDestinationItem = (prediction, index, isHighlighted = false) => {
    const predictionId = geocoder.getPredictionId(prediction);
    const truncatedPrediction = {
      ...prediction,
      place_name: truncateText(prediction?.place_name),
      description: truncateText(prediction?.description),
    };

    return (
      <li
        className={classNames(css.destinationItem, { [css.highlighted]: isHighlighted })}
        key={predictionId || prediction.place_id}
        data-elbaction={`search select search_id:${prediction.name} type:destination`}
        onClick={e => handleItemClick(prediction, e)}
        onMouseDown={handleItemMouseDown}
        onKeyDown={e => handleItemKeyDown(prediction, e)}
        tabIndex={isHighlighted ? 0 : -1}
        role="option"
        aria-selected={isHighlighted}
      >
        <div className={css.destinationInfo}>
          <img className={css.destinationImg} src={truncatedPrediction.image} />
          <span className={css.destinationName}>{truncatedPrediction.name}</span>
        </div>
      </li>
    );
  };

  const renderSection = (title, items) => {
    if (!items || items.length === 0) return null;

    return (
      <div className={css.section} role="group" aria-label={title}>
        <h3 className={css.sectionTitle}>{title}</h3>
        <ul className={css.sectionList} role="listbox">
          {items.map((item, index) =>
            renderDestinationItem(item, index, index === highlightedIndex)
          )}
        </ul>
      </div>
    );
  };

  const getLocalizedText = key => {
    const texts = {
      mostBookedDestinations: {
        en: 'Most Booked Destinations',
        de: 'Beliebte Reiseziele',
      },
      allDestinations: {
        en: 'All Destinations',
        de: 'Alle Reiseziele',
      },
      destinationNotAvailable: {
        en: 'Destination not available.',
        de: 'Ziel nicht verfügbar.',
      },
      noSocialbnbMessage: {
        en: "In this country there is no socialbnb yet, why don't you try a different destination.",
        de:
          'In diesem Land gib es leider noch kein socialbnb, versuch es doch mit einer anderen Destination',
      },
    };

    return texts[key]?.[lang] || texts[key]?.en || '';
  };

  const prepareDestinationsData = () => {
    const continentsData = lang === 'de' ? continentsListDe : continentsList;

    const addStaticDataToPrediction = dest => {
      const staticData = getStaticCountryData(dest.nameEn);
      const displayName = lang === 'de' ? dest.nameDe : dest.nameEn;
      
      // If we have static data, add it as predictionPlace to avoid API calls
      if (staticData) {
        return {
          ...dest,
          name: displayName,
          predictionPlace: {
            address: displayName,
            bounds: staticData.bounds,
            origin: staticData.origin,
            country: staticData.countryCode,
          },
        };
      }
      
      // Fallback: no static data, will use geocoder
      return {
        ...dest,
        name: displayName,
      };
    };

    const mostBooked = mostBookedDestinations.map(dest => ({
      ...addStaticDataToPrediction(dest),
      section: 'mostBooked',
    }));

    const continentDestinations = Object.entries(continentsData).flatMap(([key, continent]) =>
      continent.countries.map(country => ({
        ...addStaticDataToPrediction(country),
        section: 'continent',
        continentTitle: lang === 'de' ? continent.titleDe : continent.titleEn,
      }))
    );

    return { mostBooked, continentDestinations };
  };

  const renderMostBookedSection = mostBooked => {
    if (mostBooked.length === 0) return null;

    return (
      <div
        className={css.section}
        role="group"
        aria-label={getLocalizedText('mostBookedDestinations')}
      >
        <h3 className={css.sectionTitle}>{getLocalizedText('mostBookedDestinations')}</h3>
        <ul className={css.sectionList} role="listbox">
          {mostBooked.map((dest, index) =>
            renderDestinationItem(dest, index, index === highlightedIndex)
          )}
        </ul>
      </div>
    );
  };

  const renderContinentsSection = (continentDestinations, mostBookedCount) => {
    const continentGroups = continentDestinations.reduce((groups, dest) => {
      const group = groups[dest.continentTitle] || [];
      group.push(dest);
      groups[dest.continentTitle] = group;
      return groups;
    }, {});

    return (
      <div className={css.section}>
        <h3 className={css.sectionTitle}>{getLocalizedText('allDestinations')}</h3>
        {Object.entries(continentGroups).map(([continentTitle, destinations]) => (
          <div
            key={continentTitle}
            className={css.continentSection}
            role="group"
            aria-label={continentTitle}
          >
            <h4 className={css.continentTitle}>{continentTitle}</h4>
            <ul className={css.sectionList} role="listbox">
              {destinations.map((dest, index) => {
                const globalIndex = mostBookedCount + index;
                return renderDestinationItem(dest, globalIndex, globalIndex === highlightedIndex);
              })}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  const renderDefaultView = () => {
    const { mostBooked, continentDestinations } = prepareDestinationsData();

    return (
      <>
        {renderMostBookedSection(mostBooked)}
        {renderContinentsSection(continentDestinations, mostBooked.length)}
      </>
    );
  };

  const renderNoResults = () => (
    <div className={css.noResults}>
      <p>{getLocalizedText('destinationNotAvailable')}</p>
      <p style={{ fontSize: '14px' }}>{getLocalizedText('noSocialbnbMessage')}</p>
    </div>
  );

  const getContainerClasses = () => {
    const predictionRootMapProviderClass = isGoogleMapsInUse
      ? css.predictionsRootGoogle
      : css.predictionsRootMapbox;

    return isSmallScreen
      ? classNames()
      : classNames(
          rootClassName || css.predictionsRoot,
          predictionRootMapProviderClass,
          className,
          css.predictions
        );
  };

  const containerClasses = getContainerClasses();
  const searchString = typeof search === 'string' ? search : '';
  const hasActiveSearch = searchString && searchString.trim() !== '';
  const hasSearchResults = predictions && predictions.length > 0;

  const shouldShowDefaultView = !hasActiveSearch && hasSearchResults;

  const shouldShowNoResults = hasActiveSearch && hasSearchChanged && isPredictionsEmpty;

  return (
    <div className={containerClasses}>
      {predictionsLoading ? (
        <IconSpinner className={css.loader} />
      ) : shouldShowDefaultView ? (
        renderDefaultView()
      ) : hasSearchResults ? (
        <div className={css.predictionsContainer}>
          {predictions.map((prediction, index) =>
            renderDestinationItem(prediction, index, index === highlightedIndex)
          )}
        </div>
      ) : shouldShowNoResults ? (
        renderNoResults()
      ) : null}
      {children}
    </div>
  );
};

export default LocationPredictionsList;

LocationPredictionsList.defaultProps = {
  rootClassName: null,
  className: null,
  highlightedIndex: null,
  lang: 'en',
  search: '',
};

LocationPredictionsList.propTypes = {
  rootClassName: string,
  className: string,
  children: node,
  predictions: arrayOf(object).isRequired,
  geocoder: object.isRequired,
  highlightedIndex: number,
  onSelectStart: func.isRequired,
  onSelectMove: func.isRequired,
  onSelectEnd: func.isRequired,
  lang: string,
  search: string,
  history: shape({
    push: func.isRequired,
  }),
};
